import { useEffect, useMemo, useState } from 'react';
import { Typography } from '@mui/material';

import DefaultNumber from 'src/components/common/DefaultNumber';
import NumberTypo from 'src/components/common/Number';
import Tooltip from 'src/components/common/Tooltip';
import DelegateTxSelectionPopover from 'src/components/DelegateTxSelectionPopover';
import GradientButton from 'src/components/GradientButton';
import { DYDX_TOKEN, USDC_TOKEN } from 'src/constants/tokens';
import { COSMOSTATION_OPERATOR_ADDRESS } from 'src/constants/validator';
import { useCosmosBalanceSWR } from 'src/hooks/SWR/cosmos/useCosmosBalanceSWR';
import { useDelegationSWR } from 'src/hooks/SWR/cosmos/useDelegationSWR';
import { useRewardsSWR } from 'src/hooks/SWR/cosmos/useRewardsSWR';
import { useUnbondingSWR } from 'src/hooks/SWR/cosmos/useUnbondingSWR';
import { useValidatorsSWR } from 'src/hooks/SWR/cosmos/useValidatorsSWR';
import { useCurrentAccount } from 'src/hooks/useAccount';
import { useWindowSize } from 'src/hooks/useWindowSize';
import type { Validator } from 'src/types/validator';
import { pageView } from 'src/utils/ga';
import { fix, gt, plus, toDisplayDenomAmount } from 'src/utils/number';
import { isEqualsIgnoringCase } from 'src/utils/string';

import DefaultStaking from './components/DefaultStaking';
import { DefaultStakingContainer } from './components/DefaultStaking/styled';
import ClaimAllBottomSheet from './components/TxBottomSheet/ClaimAllBottomSheet';
import ClaimBottomSheet from './components/TxBottomSheet/ClaimBottomSheet';
import RestakeBottomSheet from './components/TxBottomSheet/RestakeBottomSheet';
import StakeBottomSheet from './components/TxBottomSheet/StakeBottomSheet';
import UnstakeBottomSheet from './components/TxBottomSheet/UnstakeBottomSheet';
import ValidatorButtonItem from './components/ValidatorButton';
import {
  AmountContainer,
  Base02TextContainer,
  BodyContainer,
  DYDXTopIconContainer,
  DYDXTopLeftAmountContainer,
  DYDXTopLeftContainer,
  PathwayContainer,
  RewardContainer,
  RewardTokenContainer,
  RewardTokenIconContainer,
  ScrollContainer,
  StakingStatusBodyContainer,
  StakingStatusBodyTextContainer,
  StakingStatusContainer,
  StyledReverseGradientIconTextButton,
  SwapButtonContainer,
  TopHeaderContainer,
  TopStakingAmountContainer,
  WrapperContainer,
} from './styled';

import { ReactComponent as DefaultTokenIcon } from 'src/assets/svg/DefaultToken.svg';
import { ReactComponent as DYDXIcon } from 'src/assets/svg/DYDX.svg';
import { ReactComponent as USDCIcon } from 'src/assets/svg/USDC.svg';

export default function Staking() {
  const { currentAccount } = useCurrentAccount();

  const windowSize = useWindowSize();

  const [isOpenStakeBottomsheet, setIsOpenStakeBottomsheet] = useState(false);
  const [isOpenUnstakeBottomsheet, setIsOpenUnstakeBottomsheet] = useState(false);
  const [isOpenRestakeBottomsheet, setIsOpenRestakeBottomsheet] = useState(false);
  const [isOpenClaimBottomsheet, setIsOpenClaimBottomsheet] = useState(false);
  const [isOpenClaimAllBottomsheet, setIsOpenClaimAllBottomsheet] = useState(false);

  const [isOpenDelegateTxSelectionPopover, setIsOpenDelegateTxSelectionPopover] = useState(false);

  const [selectedValidator, setSelectedValidator] = useState<Validator>();

  const halfWindowSize = useMemo(() => windowSize.width / 2, [windowSize.width]);

  const halfWindowHeight = useMemo(() => windowSize.height / 2, [windowSize.height]);

  const validators = useValidatorsSWR();

  const balance = useCosmosBalanceSWR(currentAccount.cosmosAddress);

  const baseCoinAmount = useMemo(() => balance.data?.balances?.find((item) => item.denom === DYDX_TOKEN.denom)?.amount || '0', [balance.data?.balances]);

  const delegation = useDelegationSWR({ cosmosAddress: currentAccount.cosmosAddress });

  const rewards = useRewardsSWR({ cosmosAddress: currentAccount.cosmosAddress });

  const unbondings = useUnbondingSWR({ cosmosAddress: currentAccount.cosmosAddress });

  const delegatedValidators = useMemo(
    () =>
      delegation.data?.delegation_responses
        ?.map((item) => {
          const rewardsData = rewards.data?.rewards?.find((reward) => isEqualsIgnoringCase(reward.validator_address, item.delegation.validator_address));
          const validatorData = validators.data?.validators.find((validator) =>
            isEqualsIgnoringCase(validator.operator_address, item.delegation.validator_address),
          );
          return {
            ...item,
            reward: rewardsData,
            validator: validatorData,
          };
        })
        .sort((a, b) => (gt(a.balance.amount, b.balance.amount) ? -1 : 1))
        .sort((item) => (isEqualsIgnoringCase(item.validator?.operator_address, COSMOSTATION_OPERATOR_ADDRESS) ? -1 : 1)),
    [delegation.data?.delegation_responses, rewards.data?.rewards, validators.data?.validators],
  );

  const unbondingValidators = useMemo(
    () =>
      unbondings.data?.unbonding_responses.map((item) => {
        const validatorData = validators.data?.validators.find((validator) => isEqualsIgnoringCase(validator.operator_address, item.validator_address));
        return {
          ...item,
          validator: validatorData,
        };
      }),
    [unbondings.data?.unbonding_responses, validators.data?.validators],
  );

  // NOTE totalDelegationAmount
  // const totalDelegationAmount = useMemo(
  //   () =>
  //     delegation?.data?.delegation_responses
  //       ?.filter((item) => item.balance.denom === DYDX_TOKEN.denom)
  //       ?.reduce((ac, cu) => plus(ac, cu.balance.amount), '0')
  //       .toString() || '0',
  //   [delegation?.data],
  // );

  const rewardTokens = useMemo(
    () =>
      rewards.data?.total?.map((item) => {
        const token = item.denom === DYDX_TOKEN.denom ? DYDX_TOKEN : item.denom === USDC_TOKEN.denom ? USDC_TOKEN : undefined;
        const tokenIcon = item.denom === DYDX_TOKEN.denom ? <DYDXIcon /> : item.denom === USDC_TOKEN.denom ? <USDCIcon /> : <DefaultTokenIcon />;
        const displayRewardAmount = toDisplayDenomAmount(
          item.amount,
          item.denom === DYDX_TOKEN.denom ? DYDX_TOKEN.decimals : item.denom === USDC_TOKEN.denom ? USDC_TOKEN.decimals : 6,
        );

        return {
          ...item,
          tokenIcon,
          displayRewardAmount,
          token,
        };
      }),
    [rewards.data?.total],
  );

  const errorMessage = useMemo(() => {
    if (!currentAccount.cosmosAddress) {
      return 'Please connect wallet';
    }

    if (!gt(baseCoinAmount, '0')) {
      return 'Insufficient DYDX balance for transaction';
    }

    return '';
  }, [baseCoinAmount, currentAccount.cosmosAddress]);

  useEffect(() => {
    pageView('Staking', 'Staking Management page');
  }, []);

  return (
    <>
      <TopHeaderContainer>
        <DYDXTopLeftContainer>
          <DYDXTopIconContainer>
            <DYDXIcon />
          </DYDXTopIconContainer>
          <Typography variant="h7">Available</Typography>
          &nbsp;
          <Typography variant="h4">:</Typography>
          &nbsp;
          <DYDXTopLeftAmountContainer>
            &nbsp;
            <NumberTypo typoOfIntegers="h6" typoOfDecimals="h8">
              {String(parseFloat(fix(toDisplayDenomAmount(baseCoinAmount, DYDX_TOKEN.decimals), 6)))}
            </NumberTypo>
          </DYDXTopLeftAmountContainer>
        </DYDXTopLeftContainer>
      </TopHeaderContainer>

      {gt(baseCoinAmount, '0') ? (
        <ScrollContainer>
          <BodyContainer>
            <TopStakingAmountContainer>
              <Typography variant="h6">Staked</Typography>
              &nbsp;
              <Base02TextContainer>
                <Typography variant="h6">{delegatedValidators?.length}</Typography>
              </Base02TextContainer>
            </TopStakingAmountContainer>

            <PathwayContainer>
              {delegatedValidators?.map((item, index) => (
                <ValidatorButtonItem
                  key={item.validator?.operator_address || `${index}`}
                  validator={item.validator}
                  leftTitle="Staked"
                  leftTitleValue={item.balance.amount}
                  rightTitle="Reward"
                  rightTitleValue={item.reward}
                  onClickValidator={(isClicked) => {
                    setIsOpenDelegateTxSelectionPopover(isClicked);
                    setSelectedValidator(item.validator);
                  }}
                />
              ))}
            </PathwayContainer>
            {gt(unbondingValidators?.length || '0', '0') && (
              <>
                <TopStakingAmountContainer>
                  <Typography variant="h6">Unstaking</Typography>
                  &nbsp;
                  <Base02TextContainer>
                    <Typography variant="h6">{unbondingValidators?.length}</Typography>
                  </Base02TextContainer>
                </TopStakingAmountContainer>

                <PathwayContainer>
                  {unbondingValidators?.map((item) => (
                    <ValidatorButtonItem
                      key={item.validator?.operator_address}
                      validator={item.validator}
                      leftTitle="Unstaking"
                      leftTitleValue={item.entries.reduce((ac, cu) => plus(ac, cu.balance), '0')}
                      isUndelegate
                      onClickValidator={(isClicked) => {
                        setIsOpenDelegateTxSelectionPopover(isClicked);
                      }}
                    />
                  ))}
                </PathwayContainer>
              </>
            )}

            <RewardContainer>
              <TopStakingAmountContainer>
                <Typography variant="h6">Total Rewards</Typography>
              </TopStakingAmountContainer>
              <StakingStatusContainer>
                <>
                  <StakingStatusBodyContainer>
                    {rewardTokens?.map((item) => (
                      <StakingStatusBodyTextContainer key={item.denom}>
                        <RewardTokenContainer>
                          <RewardTokenIconContainer>{item.tokenIcon}</RewardTokenIconContainer>
                          <Typography variant="h6">{item.token?.symbol}</Typography>
                        </RewardTokenContainer>
                        <AmountContainer>
                          <DefaultNumber typoOfIntegers="h4" typoOfDecimals="h6">
                            {item.displayRewardAmount}
                          </DefaultNumber>
                        </AmountContainer>
                      </StakingStatusBodyTextContainer>
                    ))}
                  </StakingStatusBodyContainer>
                  <StyledReverseGradientIconTextButton
                    text="Claim All"
                    typoVarient="h8"
                    onClick={() => {
                      setIsOpenClaimAllBottomsheet(true);
                    }}
                  />
                </>
              </StakingStatusContainer>
            </RewardContainer>
          </BodyContainer>

          <SwapButtonContainer>
            <Tooltip varient="error" title={errorMessage} placement="top" arrow>
              <span>
                <GradientButton
                  disabled={!!errorMessage}
                  onClick={() => {
                    setIsOpenStakeBottomsheet(true);
                  }}
                >
                  <Typography variant="h4">STAKE DYDX</Typography>
                </GradientButton>
              </span>
            </Tooltip>
          </SwapButtonContainer>
        </ScrollContainer>
      ) : (
        <>
          <DefaultStakingContainer>
            <DefaultStaking />
          </DefaultStakingContainer>

          <SwapButtonContainer>
            <Tooltip varient="error" title={errorMessage} placement="top" arrow>
              <div>
                <GradientButton
                  disabled={!!errorMessage}
                  onClick={() => {
                    setIsOpenStakeBottomsheet(true);
                  }}
                >
                  <Typography variant="h4">STAKE DYDX</Typography>
                </GradientButton>
              </div>
            </Tooltip>
          </SwapButtonContainer>
        </>
      )}

      <WrapperContainer data-is-open={isOpenStakeBottomsheet}>
        <StakeBottomSheet
          open={isOpenStakeBottomsheet}
          variant="persistent"
          title="Stake dYdX"
          selectedValidatorAddress={COSMOSTATION_OPERATOR_ADDRESS}
          onClose={() => {
            setIsOpenStakeBottomsheet(false);
          }}
        />
      </WrapperContainer>

      <WrapperContainer data-is-open={isOpenUnstakeBottomsheet}>
        <UnstakeBottomSheet
          open={isOpenUnstakeBottomsheet}
          variant="persistent"
          selectedValidatorAddress={selectedValidator?.operator_address || ''}
          onClose={() => {
            setIsOpenUnstakeBottomsheet(false);
          }}
        />
      </WrapperContainer>

      <WrapperContainer data-is-open={isOpenRestakeBottomsheet}>
        <RestakeBottomSheet
          open={isOpenRestakeBottomsheet}
          variant="persistent"
          selectedValidatorAddress={selectedValidator?.operator_address || ''}
          onClose={() => {
            setIsOpenRestakeBottomsheet(false);
          }}
        />
      </WrapperContainer>

      <WrapperContainer data-is-open={isOpenClaimBottomsheet}>
        <ClaimBottomSheet
          open={isOpenClaimBottomsheet}
          variant="persistent"
          selectedValidatorAddress={selectedValidator?.operator_address || ''}
          onClose={() => {
            setIsOpenClaimBottomsheet(false);
          }}
        />
      </WrapperContainer>

      <WrapperContainer data-is-open={isOpenClaimAllBottomsheet}>
        <ClaimAllBottomSheet
          open={isOpenClaimAllBottomsheet}
          variant="persistent"
          onClose={() => {
            setIsOpenClaimAllBottomsheet(false);
          }}
        />
      </WrapperContainer>

      <DelegateTxSelectionPopover
        marginThreshold={0}
        anchorReference="anchorPosition"
        anchorPosition={{ top: halfWindowHeight, left: halfWindowSize }}
        open={isOpenDelegateTxSelectionPopover}
        onClose={() => setIsOpenDelegateTxSelectionPopover(false)}
        onClickUnstake={(isClickedUnstake) => {
          setIsOpenUnstakeBottomsheet(isClickedUnstake);
          setIsOpenDelegateTxSelectionPopover(false);
        }}
        onClickRedelegate={(isClickedRestake) => {
          setIsOpenRestakeBottomsheet(isClickedRestake);
          setIsOpenDelegateTxSelectionPopover(false);
        }}
        onClickClaim={(isClickedClaim) => {
          setIsOpenClaimBottomsheet(isClickedClaim);
          setIsOpenDelegateTxSelectionPopover(false);
        }}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
      />
    </>
  );
}
